import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Phone, MessageCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NAV, CONTACT, TREATMENT_MENU } from "@/data/site";
import { CtaPair, CallButton } from "@/components/common/CtaButtons";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
    <img
      src="/nowtreat-logo.png"
      alt="NowTreat Dental, Skin & Hair Clinic — Dr. Ankesh Seth, Varanasi"
      className="h-11 w-auto"
      width="78"
      height="44"
    />
    <span className="leading-tight">
      <span className="block font-display text-lg font-bold text-brand-dark whitespace-nowrap">Dr Ankesh Kr. Seth</span>
      <span className="block text-[11px] font-medium uppercase tracking-[0.14em] text-brand-muted whitespace-nowrap">
        Dental · Skin · Hair Clinic
      </span>
    </span>
  </Link>
);

// Wide 3-column mega menu for the Treatments nav item (Dental / Skin / Hair)
const MegaTreatments = () => (
  <DropdownMenu>
    <DropdownMenuTrigger
      data-testid="nav-treatments"
      className="flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium text-brand-dark outline-none transition-colors hover:text-brand-blue"
    >
      Treatments
      <ChevronDown className="h-3.5 w-3.5" />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="start"
      data-testid="treatments-mega-menu"
      className="max-h-[80vh] w-[min(92vw,760px)] overflow-y-auto p-5"
    >
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        {TREATMENT_MENU.map((group) => (
          <div key={group.heading}>
            <Link
              to={group.to}
              data-testid={`mega-heading-${group.heading.toLowerCase().replace(/\s+/g, "-")}`}
              className="mb-2 block border-b border-brand-line pb-2 font-display text-sm font-semibold uppercase tracking-wide text-brand-blue hover:underline"
            >
              {group.heading}
            </Link>
            <div className="flex flex-col">
              {group.items.map((c) => (
                <DropdownMenuItem key={c.to} asChild className="cursor-pointer">
                  <Link to={c.to} data-testid={`nav-link-${c.to}`} className="text-sm text-brand-dark">
                    {c.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </div>
          </div>
        ))}
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-brand-line/60 bg-white/85 backdrop-blur-xl"
          : "border-white/50 bg-white/85 backdrop-blur-xl"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-6 py-3.5 md:px-8 2xl:px-12">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-0 xl:flex">
          {NAV.map((item) => {
            if (item.label === "Treatments") return <MegaTreatments key={item.label} />;
            return item.children ? (
              <DropdownMenu key={item.label}>
                <DropdownMenuTrigger
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium text-brand-dark outline-none transition-colors hover:text-brand-blue"
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-[70vh] w-64 overflow-y-auto">
                  <DropdownMenuItem asChild>
                    <Link to={item.to} className="font-semibold text-brand-blue">
                      View all {item.label}
                    </Link>
                  </DropdownMenuItem>
                  {item.children.map((c) => (
                    <DropdownMenuItem key={c.to} asChild>
                      <Link to={c.to} data-testid={`nav-link-${c.to}`}>{c.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors hover:text-brand-blue ${
                  pathname === item.to ? "text-brand-blue" : "text-brand-dark"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 xl:flex">
          <CallButton label="Book Appointment" testid="book-appointment-button" className="!px-5" />
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-cta-button"
            aria-label="Chat on WhatsApp"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-green text-brand-accent transition-all duration-300 hover:bg-brand-greenhover hover:shadow-[0_10px_30px_-8px_rgba(126,218,88,0.7)] active:scale-95"
          >
            <MessageCircle className="h-5 w-5" />
          </a>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 xl:hidden">
          <a
            href={CONTACT.phoneTel}
            data-testid="mobile-header-call"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue text-white"
          >
            <Phone className="h-4 w-4" />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-line text-brand-dark"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] overflow-y-auto sm:w-96">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="mt-6 flex flex-col gap-1">
                {NAV.map((item) =>
                  item.label === "Treatments" ? (
                    <div key={item.label} className="border-b border-brand-line/70 py-1">
                      <Link
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className="block py-3 font-display text-xl font-semibold text-brand-dark"
                      >
                        Treatments
                      </Link>
                      {TREATMENT_MENU.map((group) => (
                        <div key={group.heading} className="pb-3 pl-3">
                          <Link
                            to={group.to}
                            onClick={() => setOpen(false)}
                            className="mt-2 block text-xs font-semibold uppercase tracking-wide text-brand-blue"
                          >
                            {group.heading}
                          </Link>
                          <div className="mt-1 flex flex-col gap-1">
                            {group.items.map((c) => (
                              <Link
                                key={c.to}
                                to={c.to}
                                onClick={() => setOpen(false)}
                                className="py-1 text-sm text-brand-muted hover:text-brand-blue"
                              >
                                {c.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div key={item.label} className="border-b border-brand-line/70 py-1">
                      <Link
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className="block py-3 font-display text-xl font-semibold text-brand-dark"
                      >
                        {item.label}
                      </Link>
                      {item.children && (
                        <div className="flex flex-col gap-1 pb-3 pl-3">
                          {item.children.map((c) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              onClick={() => setOpen(false)}
                              className="py-1 text-sm text-brand-muted hover:text-brand-blue"
                            >
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
              <div className="mt-6">
                <CtaPair />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
